import { useApolloClient, useLazyQuery } from "@apollo/client";
import { useCallback } from "react";
import useUploadProgress from "./useUploadProgress";

const useDocumentUpload = ({ query, refetchQueries }) => {
  const client = useApolloClient();
  const [getUploadUrls, { loading }] = useLazyQuery(query, {
    fetchPolicy: "network-only",
  });

  const {
    progress,
    isUploading,
    failed,
    start: startUpload,
    reset,
  } = useUploadProgress();

  const upload = useCallback(
    async ({ variables, files }) => {
      const res = await getUploadUrls({ variables });

      const queryName = Object.keys(res?.data ?? {})[0];
      const result = res?.data?.[queryName];
      const success = result?.success;
      const urls = result?.urls;

      if (!success || !urls?.length) {
        return { success: false };
      }

      const { fetchFns, fetchFiles } = urls.reduce(
        (acc, urlEntry, idx) => {
          const file = files[idx];
          if (!file) return acc;

          acc.fetchFiles.push(file);
          acc.fetchFns.push(async (fileArg, { signal }) => {
            await fetch(urlEntry.url, {
              method: "PUT",
              body: fileArg,
              signal,
            });
          });
          return acc;
        },
        { fetchFns: [], fetchFiles: [] },
      );

      if (fetchFns.length > 0) {
        await startUpload(fetchFns, fetchFiles);
        await client.refetchQueries({ include: refetchQueries });
      }

      return { success: true };
    },
    [getUploadUrls, startUpload],
  );

  const retry = useCallback(async () => {
    await startUpload(
      failed.map(({ fn }) => fn),
      failed.map(({ file }) => file),
    );
  }, [failed, startUpload]);

  return {
    upload,
    retry,
    loading,
    progress,
    isUploading,
    failed,
    reset,
  };
};

export default useDocumentUpload;
